import { useState, useCallback, useMemo, useEffect } from 'react'
import { Map, Set } from 'immutable'
import { useDispatch } from 'react-redux'
import {
  prop,
  path,
  groupBy,
  cond,
  includes,
  always,
  T,
  values,
  flatten,
  compose,
  map,
  identity,
  evolve,
  curry,
} from 'ramda'

import {
  mergeWith,
  safeToJS,
  add,
  union,
  deleteVal,
  subtract,
  evolveWith,
} from 'utils/immutable'

import { getDonorFeature, mergeDonorLogic } from 'actions/feature/features'
import { clearSearchResults } from 'actions/deprecated/search'

import { useSelector } from 'hooks/redux'
import { getFeaturesRouteMatch } from 'selectors/router'
import { getBatchImportFeatureData } from 'selectors/feature/batchImport'

import { FEATURE } from 'utils/constants'
import strings from 'utils/strings'
import { insertIf } from 'utils/types'

export const SEARCH = 'SEARCH'
export const SELECT = 'SELECT'
export const REVIEW = 'REVIEW'

export const STAGES = {
  SEARCH: 0,
  SELECT: 1,
  REVIEW: 2,
}

const createSteps = ({ step, backToSearch, backToResults }) => [
  {
    title: strings.search,
    onClick: backToSearch,
  },
  {
    title: strings.select,
    onClick: step < 1 ? identity : backToResults,
    disabled: step < 1,
  },
  {
    title: strings.import,
    disabled: step < 2,
  },
]

export const createButtonConfig = ({
  stage,
  selectedFeatureId,
  handleCancel,
  confirmSelectedFeature,
  handleExecuteBatchImport,
  backToSearch,
  backToResults,
  hasSelections,
}) => [
  {
    text: strings.cancel,
    type: 'secondary',
    onClick: handleCancel,
  },
  ...insertIf(stage === SEARCH, {
    text: strings.select_feature,
    onClick: confirmSelectedFeature,
    disabled: !selectedFeatureId,
    type: 'primary',
  }),
  ...insertIf(stage === SELECT, {
    text: strings.select_feature,
    onClick: confirmSelectedFeature,
    type: 'primary',
  }),
  ...insertIf(
    stage === REVIEW,
    {
      text: strings.back,
      iconProps: {
        icon: 'chevron_left',
        fill: 'blueSecondary',
      },
      onClick: backToResults,
      type: 'secondary',
    },
    {
      text: strings.import_feature,
      onClick: handleExecuteBatchImport,
      type: 'primary',
      disabled: !hasSelections,
    }
  ),
]

const FUNCTIONS = 'functions'
const OUTPUTS = 'outputs'
const QUERIES = 'queries'

const defaultSelectedLogicMap = Map({
  [FUNCTIONS]: Set(),
  [OUTPUTS]: Set(),
  [QUERIES]: Set(),
})

const determineGrouping = cond([
  [includes('function'), always(FUNCTIONS)],
  [includes('queries'), always(QUERIES)],
  [T, always(OUTPUTS)],
])

const flattenGroupedSelections = evolve({
  [FUNCTIONS]: map(prop('uri')),
  [OUTPUTS]: map(prop('uri')),
  [QUERIES]: map(prop('uri')),
})

const getGroupFromSingleRecord = compose(determineGrouping, prop('uri'))

const groupSelections = groupBy(getGroupFromSingleRecord)

const createRowSelectionKeysArr = compose(flatten, values, safeToJS)

const deleteUriFromSetOfUris = compose(deleteVal, prop('uri'))
const addUriToSetOfUris = compose(add, prop('uri'))

const addParentFunctionToSet = compose(add, prop('parentFunction'))

const maybeRemoveParentFunctionFromSet = curry(
  (record, currentSelections, collection) => {
    const siblings = prop('siblings', record)
    if (
      siblings.every(
        (sibling) => !currentSelections.get('outputs').has(sibling)
      )
    ) {
      return collection.delete(prop('parentFunction', record))
    }
    return collection
  }
)

const uniteSiblingsWithSet = compose(union, prop('siblings'))
const subtractSiblingsWithSet = compose(subtract, prop('siblings'))

const deselectTransformationDictionary = {
  [FUNCTIONS]: {
    [FUNCTIONS]: deleteUriFromSetOfUris,
    [QUERIES]: compose(subtract, path(['dependents', 'queries'])),
    [OUTPUTS]: compose(subtract, path(['dependents', 'outputs'])),
  },
  [OUTPUTS]: {
    [OUTPUTS]: deleteUriFromSetOfUris,
    [FUNCTIONS]: maybeRemoveParentFunctionFromSet,
  },
  [QUERIES]: {
    [QUERIES]: subtractSiblingsWithSet,
  },
}

const selectTransformationDictionary = {
  [FUNCTIONS]: {
    [FUNCTIONS]: addUriToSetOfUris,
    [QUERIES]: compose(union, path(['dependents', 'queries'])),
    [OUTPUTS]: compose(union, path(['dependents', 'outputs'])),
  },
  [OUTPUTS]: {
    [FUNCTIONS]: addParentFunctionToSet,
    [OUTPUTS]: addUriToSetOfUris,
  },
  [QUERIES]: {
    [QUERIES]: uniteSiblingsWithSet,
  },
}

const prepSelectedLogicForMerge = (selectedLogic) =>
  selectedLogic.update(FUNCTIONS, (functionSet) =>
    functionSet.filter((key) => !key.includes('placeholder'))
  )

const handleUpdateSelectionsOnSelect = (
  record,
  selected,
  currentSelections
) => {
  const group = getGroupFromSingleRecord(record)
  const transformDictionary = selected
    ? selectTransformationDictionary
    : deselectTransformationDictionary
  const transformations = transformDictionary[group]
  return evolveWith(transformations, record, currentSelections)
}

export default function useBatchImportHook(onClose) {
  const dispatch = useDispatch()
  const match = useSelector(getFeaturesRouteMatch)
  const currentFeatureId = path(['params', 'featureId'], match)
  const [stage, setStage] = useState(SEARCH)
  const [selectedFeatureId, setSelectedFeatureId] = useState(null)
  const [selectedPartNumber, setSelectedPartNumber] = useState(null)

  const { data, featureDescription } = useSelector(getBatchImportFeatureData, {
    featureId: selectedFeatureId,
  })

  const [selectedLogic, setSelectedLogic] = useState(null)

  useEffect(() => {
    if (data && !selectedLogic) {
      setSelectedLogic(defaultSelectedLogicMap)
    }
  }, [data, selectedLogic])

  const confirmSelectedFeature = useCallback(() => {
    setStage(REVIEW)
    dispatch(getDonorFeature(selectedFeatureId))
  }, [selectedFeatureId, dispatch])

  const backToSearch = useCallback(() => {
    setStage(SEARCH)
    setSelectedFeatureId(null)
    setSelectedPartNumber(null)
    setSelectedLogic(null)
  }, [])

  const backToResults = useCallback(() => {
    setStage(SELECT)
  }, [])

  const handleCancel = useCallback(() => {
    onClose()
    setStage(SEARCH)
    setSelectedFeatureId(null)
    setSelectedPartNumber(null)
    dispatch(clearSearchResults(FEATURE, false))
  }, [dispatch, onClose])

  const handleSelectFeature = useCallback((featureObj) => {
    setSelectedFeatureId(prop('id', featureObj))
    setSelectedPartNumber(prop('partNumber', featureObj))
    setSelectedLogic(null)
    setStage(SELECT)
  }, [])

  const handleExecuteBatchImport = useCallback(() => {
    dispatch(
      mergeDonorLogic(
        currentFeatureId,
        prepSelectedLogicForMerge(selectedLogic),
        selectedFeatureId
      )
    )
    setSelectedLogic(null)
    onClose()
  }, [onClose, dispatch, selectedLogic, currentFeatureId, selectedFeatureId])

  const selectedRowKeys = useMemo(() => {
    if (!selectedLogic) {
      return []
    }
    return createRowSelectionKeysArr(selectedLogic)
  }, [selectedLogic])

  const stepperSteps = useMemo(
    () =>
      createSteps({
        step: STAGES[stage],
        backToSearch,
        backToResults,
      }),
    [stage, backToResults, backToSearch]
  )

  const logicReviewRowSelectionProps = {
    checkStrictly: true,
    onSelectAll: (_, selectedRows) => {
      compose(
        setSelectedLogic,
        mergeWith(
          (oldVal, newVal) => oldVal.union(newVal),
          defaultSelectedLogicMap
        ),
        flattenGroupedSelections,
        groupSelections
      )(selectedRows)
    },
    onSelect: (record, selected) => {
      const updatedSelections = handleUpdateSelectionsOnSelect(
        record,
        selected,
        selectedLogic
      )
      setSelectedLogic(updatedSelections)
    },
    selectedRowKeys,
    // eslint-disable-next-line consistent-return
    getCheckboxProps: (record) => {
      if (record.conflict || record.disabled) {
        return { disabled: true }
      }
    },
    // eslint-enable consistent-return
  }

  const buttonConfig = useMemo(
    () =>
      createButtonConfig({
        stage,
        handleCancel,
        selectedFeatureId,
        confirmSelectedFeature,
        backToSearch,
        handleExecuteBatchImport,
        backToResults,
        hasSelections: selectedRowKeys.length > 0,
      }),
    [
      stage,
      handleCancel,
      selectedFeatureId,
      confirmSelectedFeature,
      backToSearch,
      backToResults,
      handleExecuteBatchImport,
      selectedRowKeys,
    ]
  )

  return {
    stage,
    selectedFeatureId,
    selectedPartNumber,
    buttonConfig,
    handleSelectFeature,
    logicReviewRowSelectionProps,
    data,
    featureDescription,
    handleCancel,
    stepperSteps,
  }
}
